// ===========================================
// Custom Email Analytics Page
// ===========================================
// Display detailed analytics for a custom email campaign

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import {
  Box,
  Paper,
  Button,
  Stack,
  CircularProgress,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Link as MuiLink,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Email as SentIcon,
  DoneAll as DeliveredIcon,
  Visibility as OpenedIcon,
  TouchApp as ClickedIcon,
  Error as BouncedIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import {
  useDetailedAnalytics,
  useAnalyticsRecords,
} from '../../hooks/useCustomEmail';

// ===========================================
// STAT CARD COMPONENT
// ===========================================

interface StatCardProps {
  title: string;
  value: number;
  total?: number;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'info' | 'warning' | 'error';
  percentage?: number;
}

function StatCard({ title, value, total, icon, color, percentage }: StatCardProps) {
  const showPercentage = percentage !== undefined;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${color}.lighter`,
              color: `${color}.main`,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {value.toLocaleString()}
              {total !== undefined && (
                <Typography component="span" variant="body2" color="text.secondary">
                  {' / '}{total.toLocaleString()}
                </Typography>
              )}
            </Typography>
            {showPercentage && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(percentage, 100)}
                  color={color}
                  sx={{ height: 6, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {percentage.toFixed(1)}%
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function CustomEmailAnalyticsPage() {
  const { schoolSlug } = useTenant();
  const { id } = useParams<{ id: string }>();

  // Pagination state for delivery log
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Queries
  const { data: analytics, isLoading: isLoadingAnalytics, error: analyticsError } = useDetailedAnalytics(id || '');
  const { data: recordsData, isLoading: isLoadingRecords } = useAnalyticsRecords(id || '', {
    page: page + 1,
    limit: rowsPerPage,
  });

  if (isLoadingAnalytics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (analyticsError || !analytics) {
    return (
      <Box>
        <PageHeader
          title="Email Analytics"
          action={
            <Button startIcon={<BackIcon />} component={Link} to={`/${schoolSlug}/admin/custom-emails`}>
              Back
            </Button>
          }
        />
        <Alert severity="error">
          Failed to load analytics: {analyticsError instanceof Error ? analyticsError.message : 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  const records = recordsData?.records || [];
  const totalRecords = recordsData?.total || 0;

  return (
    <Box>
      <PageHeader
        title="Email Analytics"
        subtitle={analytics.title}
        action={
          <Button startIcon={<BackIcon />} component={Link} to={`/${schoolSlug}/admin/custom-emails`}>
            Back to Emails
          </Button>
        }
      />

      {/* Email Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6">{analytics.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              Subject: {analytics.subject}
            </Typography>
            {analytics.sentAt && (
              <Typography variant="body2" color="text.secondary">
                Sent: {new Date(analytics.sentAt).toLocaleString()}
              </Typography>
            )}
          </Box>
          <Chip
            label={analytics.sentAt ? 'Sent' : 'Not Sent'}
            color={analytics.sentAt ? 'success' : 'default'}
          />
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard
          title="Sent"
          value={analytics.totalRecipients}
          icon={<SentIcon />}
          color="primary"
        />
        <StatCard
          title="Delivered"
          value={analytics.delivered}
          total={analytics.totalRecipients}
          icon={<DeliveredIcon />}
          color="success"
          percentage={analytics.totalRecipients > 0 ? (analytics.delivered / analytics.totalRecipients) * 100 : 0}
        />
        <StatCard
          title="Opened"
          value={analytics.opened}
          icon={<OpenedIcon />}
          color="info"
          percentage={analytics.openRate}
        />
        <StatCard
          title="Clicked"
          value={analytics.clicked}
          icon={<ClickedIcon />}
          color="warning"
          percentage={analytics.clickRate}
        />
        <StatCard
          title="Bounced"
          value={analytics.bounced}
          icon={<BouncedIcon />}
          color="error"
          percentage={analytics.totalRecipients > 0 ? (analytics.bounced / analytics.totalRecipients) * 100 : 0}
        />
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Recipient Breakdown */}
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            Recipient Breakdown
          </Typography>

          {/* By Type */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              By Type
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label={`Parents: ${analytics.recipientBreakdown.byType.parents}`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`Teachers: ${analytics.recipientBreakdown.byType.teachers}`}
                color="secondary"
                variant="outlined"
              />
            </Stack>
          </Box>

          {/* By Age Group */}
          {Object.keys(analytics.recipientBreakdown.byAgeGroup).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                By Age Group
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.entries(analytics.recipientBreakdown.byAgeGroup).map(([group, count]) => (
                  <Chip key={group} label={`${group}: ${count}`} size="small" />
                ))}
              </Stack>
            </Box>
          )}

          {/* By Instrument */}
          {Object.keys(analytics.recipientBreakdown.byInstrument).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                By Instrument
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.entries(analytics.recipientBreakdown.byInstrument).map(([instrument, count]) => (
                  <Chip key={instrument} label={`${instrument}: ${count}`} size="small" />
                ))}
              </Stack>
            </Box>
          )}

          {/* By Program Level */}
          {Object.keys(analytics.recipientBreakdown.byProgramLevel).length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                By Program Level
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.entries(analytics.recipientBreakdown.byProgramLevel).map(([level, count]) => (
                  <Chip key={level} label={`${level}: ${count}`} size="small" />
                ))}
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Top Clicked URLs */}
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            Top Clicked Links
          </Typography>
          {analytics.topClickedUrls.length === 0 ? (
            <Typography color="text.secondary">No link clicks recorded yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell align="right">Clicks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.topClickedUrls.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <MuiLink
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          maxWidth: 300,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.url}
                      </MuiLink>
                    </TableCell>
                    <TableCell align="right">{item.clicks}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Stack>

      {/* Delivery Log */}
      <Paper sx={{ mt: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6">Delivery Log</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Delivered</TableCell>
                <TableCell>Opened</TableCell>
                <TableCell>Clicked</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingRecords ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">No delivery records found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{record.recipientEmail}</TableCell>
                    <TableCell>
                      <Chip
                        label={record.recipientType}
                        size="small"
                        color={record.recipientType === 'PARENT' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {record.sentAt ? new Date(record.sentAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      {record.deliveredAt ? new Date(record.deliveredAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      {record.openedAt ? new Date(record.openedAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      {record.clickedAt ? (
                        <Box>
                          <Typography variant="body2">
                            {new Date(record.clickedAt).toLocaleString()}
                          </Typography>
                          {record.clickedUrls.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {record.clickedUrls.length} link(s)
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {record.bounced ? (
                        <Chip label="Bounced" size="small" color="error" />
                      ) : record.delivered ? (
                        <Chip label="Delivered" size="small" color="success" />
                      ) : record.sentAt ? (
                        <Chip label="Sent" size="small" color="primary" />
                      ) : (
                        <Chip label="Pending" size="small" color="default" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>
    </Box>
  );
}
